// @ts-ignore

// Monkey-patch console.warn first, then require the App
// if (__DEV__) {
//   const IGNORED_WARNINGS = [
//     'Remote debugger is in a background tab which may cause apps to perform slowly',
//     'Require cycle:',
//   ]
//   const oldConsoleWarn = console.warn

//   console.warn = (...args: any[]) => {
//     if (
//       typeof args[0] === 'string' &&
//       IGNORED_WARNINGS.some((ignoredWarning) =>
//         args[0].startsWith(ignoredWarning)
//       )
//     ) {
//       return
//     }

//     // @ts-ignore
//     return oldConsoleWarn.apply(console, args)
//   }
// }

import * as Sentry from '@sentry/react'

import { createElement, useEffect } from 'react'

import { createRoot } from 'react-dom/client'
import { App } from './App'
import { NotFoundContext, NotFoundContextValue } from '@data/NotFoundContext'
import { EmbedBrowserRouter } from './newComponents/EmbedBrowserRouter'
import { loadableReady } from '@loadable/component'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { setLocale } from 'yup'

import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  Routes,
} from 'react-router-dom'

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({ enableInp: true }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Sample Rates
  sampleRate: 0.1,
  // Performance Monitoring
  tracesSampleRate: 0.1, //  Capture 10% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/guild\.host\/graphql/],
  // Session Replay
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.1,
  environment: process.env.GUILD_ENV,
  release: process.env.SENTRY_RELEASE,
  normalizeDepth: 10,
  // debug: process.env.NODE_ENV !== 'production',
  enabled: process.env.NODE_ENV === 'production',

  // Filter out errors caused by browser extensions, not our code
  ignoreErrors: [
    // Chrome extension messaging errors
    'runtime.sendMessage',
    'runtime.connect',
    // Benign ResizeObserver warnings
    /ResizeObserver loop/,
  ],

  beforeSend(event, hint) {
    const error = hint?.originalException

    // Check if the error stack contains browser extension URLs
    if (error instanceof Error && error.stack) {
      if (
        error.stack.includes('chrome-extension://') ||
        error.stack.includes('moz-extension://')
      ) {
        return null
      }
    }

    // Filter DOM manipulation NotFoundErrors with no first-party stack frames
    // (e.g. removeChild/insertBefore/appendChild errors caused by extensions)
    if (
      error instanceof Error &&
      error.name === 'NotFoundError' &&
      /removeChild|insertBefore|appendChild/.test(error.message)
    ) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames
      if (frames && frames.length > 0) {
        const hasFirstPartyFrame = frames.some(
          (frame) =>
            frame.filename &&
            !frame.filename.includes('chrome-extension://') &&
            !frame.filename.includes('moz-extension://') &&
            !frame.filename.startsWith('native') &&
            !frame.filename.includes('node_modules')
        )
        if (!hasFirstPartyFrame) {
          return null
        }
      }
    }

    return event
  },
})

const getInitialState = (async (): Promise<{
  accessToken: string | undefined
  refreshToken: string | undefined
}> => {
  try {
    const [[_, savedAccessToken], [__, savedRefreshToken]] =
      await AsyncStorage.multiGet(['guild-access-token', 'guild-refresh-token'])

    return {
      accessToken: savedAccessToken || undefined,
      refreshToken: savedRefreshToken || undefined,
    }
  } catch (err) {
    Sentry.captureException(err)

    return {
      accessToken: undefined,
      refreshToken: undefined,
    }
  }
})()

/**
 * Configure default error messages for yup validation
 */
setLocale({
  mixed: { required: 'Required field' },
  number: {
    integer: 'Must be an integer',
    positive: 'Must be a positive number',
  },
})

const main = async () => {
  try {
    await loadableReady()
  } catch (err) {
    Sentry.captureException(err)
  } finally {
    const rootElement = document.getElementById('root')

    if (!rootElement) {
      console.error('Could not find `root` element')
      return
    }

    // Client-side NotFoundContext - no-op since HTTP status is already set by SSR
    const clientNotFoundContext: NotFoundContextValue = {
      setNotFound: () => {},
      isNotFound: false,
    }

    const BrowserApp = createElement(
      NotFoundContext.Provider,
      { value: clientNotFoundContext },
      createElement(App, {
        ...(await getInitialState),
        Router: EmbedBrowserRouter as ({
          children,
        }: {
          children: React.ReactNode
        }) => JSX.Element,
      })
    )

    // Register Sentry Replay BEFORE the first render so
    // replaysOnErrorSampleRate captures errors during initial render.
    if (process.env.NODE_ENV === 'production') {
      try {
        Sentry.addIntegration(
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          })
        )
      } catch (err) {
        // Non-fatal — app works without replay
        console.error('Failed to load Sentry Replay', err)
      }
    }

    createRoot(rootElement).render(BrowserApp)
  }
}

main()
